// Story 5.4 — TanStack Query read hook for GET
// /api/v1/marketplace/sealed-dossier-listings.
//
// Sibling to useMarketplaceShareListings. Three divergences:
//   * Filter axis: drops (instead of creators + tiers).
//   * Row shape carries sealed_dossier (not share).
//   * filter_metadata carries available_drops.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import type { MarketplaceBrowseSort, MarketplacePageInfo, MarketplacePerPage } from "./useMarketplaceShareListings";

export type DropStatus = "scheduled" | "live" | "retiring" | "retired";

export interface UseMarketplaceSealedDossierListingsArgs {
  page: number;
  perPage: MarketplacePerPage;
  drops: string[];
  priceMinCents: number | null;
  priceMaxCents: number | null;
  sort: MarketplaceBrowseSort;
  enabled?: boolean;
}

export interface MarketplaceSealedDossierCardData {
  sealed_dossier_id: string;
  drop_id: string;
  edition_number: number;
  portfolio_title: string;
  /** Wax-seal base color (migration 0079); null = default bordeaux. */
  seal_color: string | null;
  drop_status: DropStatus;
  drop_price_cents: number;
  drop_retiring_at: string | null;
}

export interface MarketplaceSealedDossierListingRow {
  listing_id: string;
  price_cents: number;
  listed_at: string;
  expires_at: string | null;
  seller_anonymous: boolean;
  seller_handle: string | null;
  delta_vs_floor_cents: number;
  is_floor: boolean;
  /** Whether the seller accepts below-asking offers (Story 5.10). */
  allow_bidding: boolean;
  /** True when this listing is the viewer's own (SPA hides Buy + offer). */
  is_own_listing: boolean;
  sealed_dossier: MarketplaceSealedDossierCardData;
}

export interface AvailableDrop {
  drop_id: string;
  portfolio_title: string;
  drop_status: DropStatus;
  active_listing_count: number;
}

export interface SealedDossierListingsFilterMetadata {
  min_price_cents: number | null;
  max_price_cents: number | null;
  available_drops: AvailableDrop[];
}

export interface MarketplaceSealedDossierListingsResponseData {
  data: MarketplaceSealedDossierListingRow[];
  page_info: MarketplacePageInfo;
  filter_metadata: SealedDossierListingsFilterMetadata;
}

export interface MarketplaceSealedDossierListingsError {
  code: string;
  message: string;
  field?: string;
  status: number;
}

function buildSealedDossierListingsUrl(
  args: UseMarketplaceSealedDossierListingsArgs,
): string {
  const sp = new URLSearchParams();
  sp.set("page", String(args.page));
  sp.set("per_page", String(args.perPage));
  if (args.drops.length > 0) {
    sp.set("drop_id", args.drops.join(","));
  }
  if (args.priceMinCents != null) {
    sp.set("price_min", String(args.priceMinCents));
  }
  if (args.priceMaxCents != null) {
    sp.set("price_max", String(args.priceMaxCents));
  }
  sp.set("sort", args.sort);
  return `/api/v1/marketplace/sealed-dossier-listings?${sp.toString()}`;
}

async function fetchSealedDossierListings(
  args: UseMarketplaceSealedDossierListingsArgs,
): Promise<MarketplaceSealedDossierListingsResponseData> {
  const url = buildSealedDossierListingsUrl(args);
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: MarketplaceSealedDossierListingsError = {
      code: "BROWSE_FAILED",
      message: `Marketplace browse failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
  const body = (await response.json()) as {
    data: MarketplaceSealedDossierListingsResponseData;
  };
  return body.data;
}

export function useMarketplaceSealedDossierListings(
  args: UseMarketplaceSealedDossierListingsArgs,
): UseQueryResult<
  MarketplaceSealedDossierListingsResponseData,
  MarketplaceSealedDossierListingsError
> {
  return useQuery<
    MarketplaceSealedDossierListingsResponseData,
    MarketplaceSealedDossierListingsError
  >({
    queryKey: [
      "marketplace-sealed-dossier-listings",
      args.page,
      args.perPage,
      [...args.drops].sort(),
      args.priceMinCents,
      args.priceMaxCents,
      args.sort,
    ],
    queryFn: () => fetchSealedDossierListings(args),
    staleTime: 30 * 1000,
    enabled: args.enabled !== false,
    // Fail fast: a failing browse should surface an error immediately, not
    // sit on "Loading…" through 3 exponential-backoff retries.
    retry: false,
  });
}
